import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, ShieldCheck, Droplets, Clock, FileText } from 'lucide-react';
import { SEO } from '@/components/SEO';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { SERVICE_AREAS } from '@/data/serviceAreas';

const SITE_URL = 'https://www.venturapowerwashing.com';
const PHONE_DISPLAY = '(805) 464-7166';
const PHONE_HREF = 'tel:+18054647166';

const services = [
  {
    name: 'Pressure Washing',
    href: '/services/pressure-washing',
    body: 'Hot-water pressure cleaning for driveways, patios, walkways, and other hard surfaces. We take oil, grease, gum, and years of grime and return clean, even concrete. Typical projects run $150–$400, and most residential jobs finish the same day.',
  },
  {
    name: 'Soft Wash',
    href: '/services/soft-wash',
    body: 'Low-pressure washing with biodegradable detergents for stucco, painted exteriors, and roofs. We take homes streaked with algae, mildew, and dirt and lift the growth at the source without forcing water into the surface.',
  },
  {
    name: 'Window Cleaning',
    href: '/services/window-washing',
    body: 'Streak-free interior and exterior window cleaning for homes and storefronts. Typical projects run $150–$300.',
  },
  {
    name: 'Gutter Cleaning',
    href: '/services/gutter-cleaning',
    body: 'Debris removal and flushing so gutters and downspouts drain before the rainy season. We take clogged, overflowing gutters and leave clear, flowing ones.',
  },
  {
    name: 'Solar Panel Cleaning',
    href: '/services/solar-panel-cleaning',
    body: 'Gentle, residue-free panel cleaning that removes dust, pollen, and bird droppings that block sunlight. Typical projects run $150–$350.',
  },
  {
    name: 'Carpet Cleaning',
    href: '/services/carpet-cleaning',
    body: 'Hot water extraction for traffic lanes, pet stains, and general buildup. Typical whole-house projects run $100–$300.',
  },
  {
    name: 'Dumpster Pad Cleaning',
    href: '/services/dumpster-pad-cleaning',
    body: 'Degreasing and sanitizing of dumpster enclosures and pads for restaurants, HOAs, and commercial properties. Commercial jobs use our hot-water reclaim system so wash water is captured instead of running into the storm drain.',
  },
];

const differentiators = [
  {
    title: 'Hot Water on Every Pressure Job',
    difference: 'Our pressure washing equipment heats water, which breaks down oil, grease, and gum far faster than cold water alone.',
    negative: 'We do not treat oil-stained driveways with cold water and harsh acid.',
    comparison: 'Many local independent pressure washers run cold-water units, which often leave the stain ghost behind.',
  },
  {
    title: 'Soft Wash Where It Belongs',
    difference: 'Stucco, painted surfaces, and roofs get low pressure and detergent, never a high-pressure wand.',
    negative: 'We do not pressure wash painted siding or roofs, and we do not blast stucco.',
    comparison: 'Contractors who use high pressure on delicate surfaces can gouge stucco, strip paint, and push water behind walls.',
  },
  {
    title: 'Insured and Licensed',
    difference: 'We carry $1M–$2M general liability insurance plus full workers’ compensation, and we operate as a licensed exterior cleaning contractor.',
    negative: 'We do not send uninsured crews to your property.',
    comparison: 'Operators without insurance or licensing leave you personally exposed if something gets damaged or someone gets hurt.',
  },
  {
    title: 'Fast, Predictable Response',
    difference: 'We respond within 1 hour during business hours, deliver quotes within 24 hours, and schedule most jobs within 48–72 hours.',
    negative: 'We do not make you chase us for a quote.',
    comparison: 'National franchises route requests through call centers, which can add days before anyone looks at your property.',
  },
  {
    title: 'No Long-Term Contracts',
    difference: 'Residential work is priced per project. HOAs and commercial clients can use month-to-month or per-job agreements.',
    negative: 'We do not lock HOAs or businesses into multi-year contracts.',
    comparison: 'Some companies require long-term HOA contracts. We earn the next visit with the last one.',
  },
  {
    title: 'Water and Landscape Protection',
    difference: 'We pre-soak landscaping, manage runoff, apply neutralizers when needed, and use a hot-water reclaim system on commercial jobs.',
    negative: 'We do not use harsh acids or non-biodegradable chemicals.',
    comparison: 'Rushed crews often let runoff flow to the street and leave plants burned by strong chemicals.',
  },
];

const segments = [
  {
    name: 'Homeowners',
    detail: 'Single-family homes across Ventura County with stucco, concrete driveways, tile or composite roofs, windows, and solar panels.',
    pain: 'Stained driveways, algae on north-facing walls, streaky windows, and worry that a contractor will damage stucco.',
    trigger: 'Selling or refinancing, hosting an event, a new HOA notice, or the start of the rainy season.',
  },
  {
    name: 'HOAs and Property Managers',
    detail: 'Residential communities and multi-unit properties that need common areas, walkways, and trash enclosures kept clean.',
    pain: 'Inconsistent vendors, long contracts, and no easy way to get reliable quotes.',
    trigger: 'A board request, a resident complaint, a scheduled inspection, or a new management company.',
  },
  {
    name: 'Restaurants and Commercial Properties',
    detail: 'Restaurants, shopping centers, and businesses with dumpster pads, sidewalks, storefronts, and parking areas.',
    pain: 'Grease, odor, and stained concrete that hurt curb appeal and can draw code or health attention.',
    trigger: 'A health inspection, a landlord notice, a new tenant, or a grand opening.',
  },
];

const steps = [
  { title: 'Reach out', text: 'Call or text (805) 464-7166, use the free estimate form, or chat with us on the site. We respond within 1 hour during business hours.' },
  { title: 'Get a quote', text: 'You receive a transparent per-project quote within 24 hours. Estimates are free.' },
  { title: 'Schedule', text: 'Most jobs are on the calendar within 48–72 hours.' },
  { title: 'We do the work', text: 'We protect landscaping, use the right method for each surface, and manage runoff. Most residential jobs finish the same day; commercial work takes 24–48 hours depending on scope.' },
  { title: 'Pay your way', text: 'Card, ACH, or invoice. No long-term contract is required.' },
];

const testimonials = [
  { quote: 'Ventura Power Washing made our stucco home look brand new. Fast, professional, and extremely thorough.', who: 'Homeowner, stucco home' },
  { quote: 'Our HOA relies on Brad’s team for all exterior cleaning. They’re consistent, careful, and always deliver great results.', who: 'HOA client' },
  { quote: 'The hot-water pressure cleaning removed years of oil stains from our driveway. Highly recommend.', who: 'Homeowner, driveway cleaning' },
];

const transformations = [
  {
    before: '/lovable-uploads/e57635df-c5ff-463b-a685-e793219f7fff.webp',
    after: '/lovable-uploads/a0c5a82a-7156-4024-be0d-894ad3f62b01.webp',
    title: 'Concrete driveway',
    beforeAlt: 'Before pressure washing: stained, dirty concrete driveway in Ventura County',
    afterAlt: 'After pressure washing: clean, restored concrete driveway in Ventura County',
    caption: 'Oil, grime, and years of buildup removed from a concrete driveway.',
  },
  {
    before: '/lovable-uploads/50da41c5-f891-4836-974c-772b27c05f1a.webp',
    after: '/lovable-uploads/862089e8-1263-474b-8de2-ba77e319b7af.webp',
    title: 'Windows',
    beforeAlt: 'Before window cleaning: dirty, streaked windows',
    afterAlt: 'After window cleaning: clear, streak-free windows',
    caption: 'Streaked, spotted glass returned to clear.',
  },
  {
    before: '/lovable-uploads/41a0177b-af69-4c82-90f1-192ad6d826ce.webp',
    after: '/lovable-uploads/66ba87fc-3ed0-4ed4-a370-6afea398d84c.webp',
    title: 'Carpet',
    beforeAlt: 'Before carpet cleaning: stained, worn carpet',
    afterAlt: 'After carpet cleaning: fresh, clean carpet',
    caption: 'Stains and traffic-lane buildup lifted with hot water extraction.',
  },
];

const stats = [
  { value: '300+', label: 'residential and commercial jobs completed' },
  { value: '2 yrs', label: 'serving Ventura County (since 2024)' },
  { value: '1 hr', label: 'response time during business hours' },
  { value: '24 hrs', label: 'to receive your quote' },
];

const keyFacts: [string, React.ReactNode][] = [
  ['Company name', 'Ventura Power Washing'],
  ['Type', 'Locally owned exterior cleaning company'],
  ['Founded', '2024'],
  ['Founder', 'Brad'],
  ['Based in', 'Ventura County, California'],
  ['Website', <a key="w" href={SITE_URL} className="text-ventura-blue hover:underline">venturapowerwashing.com</a>],
  ['Phone', <a key="p" href={PHONE_HREF} className="text-ventura-blue hover:underline">{PHONE_DISPLAY}</a>],
  ['Core offering', 'Pressure washing and soft washing for homes, HOAs, and commercial properties'],
  ['Services', 'Pressure washing, soft wash, window cleaning, gutter cleaning, solar panel cleaning, carpet cleaning, dumpster pad cleaning'],
  ['Pricing', 'Transparent per-project pricing with free estimates'],
  ['Contract terms', 'No long-term contracts. HOA and commercial clients can use month-to-month or per-job agreements.'],
  ['Payment', 'Card, ACH, or invoice'],
  ['Insurance', '$1M–$2M general liability and full workers’ compensation'],
  ['Cleaning products', '100% biodegradable, plant-safe, pet-safe detergents'],
  ['Response time', 'Within 1 hour during business hours; quotes within 24 hours'],
  ['Projects completed', 'Approximately 300+'],
  ['Reviews', '5.0 stars on Yelp. Google Business Profile coming soon.'],
  ['Best known for', 'Hot-water pressure cleaning, gentle soft washing of stucco, and dependable HOA service'],
  ['Social', <span key="s"><a href="https://www.instagram.com/venturacleaningco" rel="noopener noreferrer" className="text-ventura-blue hover:underline">Instagram</a>, <a href="https://www.yelp.com/biz/ventura-power-washing-thousand-oaks" rel="noopener noreferrer" className="text-ventura-blue hover:underline">Yelp</a></span>],
];

const glossary: [string, string][] = [
  ['Pressure washing', 'Cleaning hard surfaces such as concrete with high-pressure water. Also called power washing.'],
  ['Soft wash', 'Low-pressure cleaning with detergents that break down algae, mildew, and dirt without damaging delicate surfaces.'],
  ['Hot-water pressure washing', 'Pressure washing with heated water, which dissolves oil and grease much more effectively than cold water.'],
  ['PSI', 'Pounds per square inch, the measure of water pressure. Higher PSI is not better for every surface.'],
  ['Stucco', 'A cement-based exterior finish common in Southern California. It is porous and can be damaged by high pressure.'],
  ['Biodegradable detergent', 'A cleaning solution that breaks down naturally in the environment.'],
  ['Neutralizer', 'A product applied after cleaning to balance the surface and protect nearby plants.'],
  ['Water reclamation', 'Capturing wash water so it can be filtered and disposed of properly instead of entering storm drains.'],
  ['Runoff management', 'Controlling where wash water goes during a job to protect drains, landscaping, and neighbors.'],
  ['Organic growth', 'Algae, moss, lichen, and mildew that grow on shaded or damp exterior surfaces.'],
  ['Dumpster pad', 'The concrete slab under and around a dumpster, which collects grease, liquids, and odor.'],
  ['HOA', 'Homeowners association, the board that manages shared areas of a residential community.'],
];

const misconceptions: [string, string][] = [
  ['Pressure washing is always the best option.', 'For stucco, painted siding, and roofs, high pressure can cause damage. We use soft wash on delicate surfaces.'],
  ['All power washers are the same.', 'Cold-water and hot-water equipment perform differently, especially on oil and grease.'],
  ['“Eco-friendly” means chemical-free.', 'We use 100% biodegradable, plant-safe, pet-safe detergents. We do not claim our work involves no cleaning chemicals at all.'],
  ['You need a long-term contract for HOA service.', 'We work month-to-month or per job. No long-term contract is required.'],
  ['A cleaning company can remove any mold problem.', 'We remove exterior organic growth such as algae and mildew. We do not perform mold remediation.'],
  ['Getting a quote takes days.', 'We respond within 1 hour during business hours and deliver quotes within 24 hours.'],
];

const notOffered = {
  services: ['Interior HVAC system cleaning', 'Mold remediation beyond exterior organic growth removal', 'Asbestos siding cleaning'],
  surfaces: ['Painted siding (pressure washing)', 'Roofs (pressure washing; roofs are soft washed only)', 'Stucco and other delicate surfaces at high pressure'],
  chemicals: ['Harsh acids', 'Non-biodegradable chemicals'],
  claims: ['That our detergents are “chemical-free”', 'That we perform mold remediation', 'That we work in areas outside our service area'],
  areas: ['Northern California', 'The High Desert'],
};

const cities = SERVICE_AREAS.map((a) => a.name);
const cityLinks: Record<string, string> = Object.fromEntries(SERVICE_AREAS.map((a) => [a.name, a.path]));
const neighborhoods = SERVICE_AREAS.flatMap((a) => a.neighborhoods);

const competitors = [
  {
    name: 'Independent cold-water pressure washers',
    does: 'Small operators with cold-water units, often at low prices.',
    differs: 'We use hot water, which lifts oil and grease that cold water leaves behind.',
    avoid: 'We do not chase the lowest price at the cost of results.',
    better: 'Oil stains, grease, and gum on concrete.',
  },
  {
    name: 'National franchise cleaning companies',
    does: 'Branded cleaning services run through regional call centers.',
    differs: 'You deal directly with a local, owner-run company that lives in Ventura County.',
    avoid: 'We do not run a call-center process.',
    better: 'Response speed and local knowledge of stucco, coastal air, and regional conditions.',
  },
  {
    name: 'Uninsured or unlicensed operators',
    does: 'Low-cost cleaning with little accountability.',
    differs: 'We carry $1M–$2M general liability and full workers’ compensation.',
    avoid: 'We do not cut corners on coverage.',
    better: 'Protection and peace of mind for property owners and HOAs.',
  },
  {
    name: 'High-pressure-on-everything contractors',
    does: 'Use one method on every surface.',
    differs: 'We match method to surface and soft wash delicate exteriors.',
    avoid: 'We do not use high pressure on stucco or painted siding.',
    better: 'Protecting stucco, paint, and landscaping.',
  },
  {
    name: 'Companies that require long-term HOA contracts',
    does: 'Lock communities into multi-year agreements.',
    differs: 'We work month-to-month or per job.',
    avoid: 'We do not require long-term contracts.',
    better: 'Flexibility, transparent pricing, and accountability.',
  },
];

const faqs = [
  {
    question: 'Who owns Ventura Power Washing?',
    answer: 'Ventura Power Washing was founded in 2024 by Brad, a Thousand Oaks resident who built the company to bring commercial-grade equipment, eco-safe methods, and reliable local service to Ventura County.',
  },
  {
    question: 'Is Ventura Power Washing licensed and insured?',
    answer: 'Yes. Ventura Power Washing is a licensed exterior cleaning contractor with $1M–$2M in general liability insurance and full workers’ compensation coverage.',
  },
  {
    question: 'Do you require a long-term contract?',
    answer: 'No. Residential jobs are priced per project. HOAs and commercial clients can choose month-to-month or per-job agreements.',
  },
  {
    question: 'What cleaning products do you use?',
    answer: 'We use 100% biodegradable, plant-safe, pet-safe detergents. We pre-soak landscaping, manage runoff, and apply neutralizers when needed. We do not use harsh acids or non-biodegradable chemicals.',
  },
  {
    question: 'Do you pressure wash stucco or roofs?',
    answer: 'No. We do not use high pressure on stucco or other delicate surfaces, and we do not pressure wash painted siding or roofs. We soft wash those surfaces at low pressure instead.',
  },
  {
    question: 'How fast can you give me a quote and schedule the job?',
    answer: 'We respond within 1 hour during business hours and deliver quotes within 24 hours. Most jobs are scheduled within 48–72 hours, and most residential jobs are finished in one day.',
  },
  {
    question: 'What areas do you serve?',
    answer: 'We serve Ventura County, including Ventura, Oxnard, Camarillo, Thousand Oaks, Newbury Park, Simi Valley, Moorpark, Fillmore, Port Hueneme, Ojai, Santa Paula, and Westlake Village. We do not serve Northern California or the High Desert.',
  },
];

const SectionHeading = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <h2 id={id} className="text-3xl font-bold text-gray-900 mb-6">{children}</h2>
);

const AboutPage = () => {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': `${SITE_URL}/about#webpage`,
    url: `${SITE_URL}/about`,
    name: 'About Ventura Power Washing',
    description: 'Learn who is behind Ventura Power Washing, how the company works, and what it does and does not do.',
    mainEntity: { '@id': `${SITE_URL}/#business` },
  };

  const businessSchema = {
    '@context': 'https://schema.org',
    // schema.org has no "CleaningService" type; ProfessionalService is the closest LocalBusiness subtype.
    '@type': ['LocalBusiness', 'ProfessionalService'],
    '@id': `${SITE_URL}/#business`,
    name: 'Ventura Power Washing',
    description: 'Locally owned exterior cleaning company providing pressure washing, soft wash, window, gutter, solar panel, carpet, and dumpster pad cleaning across Ventura County, California.',
    url: SITE_URL,
    logo: `${SITE_URL}/logo/ventura-power-washing-icon.png`,
    image: `${SITE_URL}/og-image.jpg`,
    telephone: PHONE_DISPLAY,
    priceRange: '$$',
    foundingDate: '2024',
    founder: { '@id': `${SITE_URL}/about#brad-goldston` },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Ventura',
      addressRegion: 'CA',
      postalCode: '93001',
      addressCountry: 'US',
    },
    geo: { '@type': 'GeoCoordinates', latitude: '34.2804', longitude: '-119.2949' },
    areaServed: [...cities, 'Ventura County'].map((name) => ({ '@type': name === 'Ventura County' ? 'AdministrativeArea' : 'City', name })),
    knowsAbout: [
      'Pressure washing', 'Soft washing', 'Hot-water pressure cleaning', 'Stucco cleaning',
      'Window cleaning', 'Gutter cleaning', 'Solar panel cleaning', 'Carpet cleaning',
      'Dumpster pad cleaning', 'Water reclamation',
    ],
    paymentAccepted: 'Credit Card, ACH, Invoice',
    sameAs: [
      'https://www.instagram.com/venturacleaningco',
      'https://www.yelp.com/biz/ventura-power-washing-thousand-oaks',
    ],
  };

  const founderSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${SITE_URL}/about#brad-goldston`,
    name: 'Brad',
    jobTitle: 'Founder',
    image: `${SITE_URL}/team/brad-goldston.jpg`,
    worksFor: { '@id': `${SITE_URL}/#business` },
    homeLocation: { '@type': 'City', name: 'Thousand Oaks' },
  };

  const loganSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${SITE_URL}/about#logan-oliverson`,
    name: 'Logan',
    jobTitle: 'Cleaning Tech',
    image: `${SITE_URL}/team/logan-oliverson.jpg`,
    worksFor: { '@id': `${SITE_URL}/#business` },
    homeLocation: { '@type': 'City', name: 'Thousand Oaks' },
    knowsAbout: ['Window cleaning', 'Pressure washing', 'Carpet cleaning'],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <>
      <SEO
        title="About Ventura Power Washing | Locally Owned Exterior Cleaning in Ventura County"
        description="Meet Brad and the team behind Ventura Power Washing. Insured, hot-water pressure washing and soft washing for Ventura County homes, HOAs, and businesses. No long-term contracts."
        path="/about"
        image={`${SITE_URL}/team/brad-goldston.jpg`}
      >
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(businessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(founderSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(loganSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </SEO>

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'About Us', href: '/about' }]} />

        <main className="flex-1">
          <header className="bg-gradient-to-br from-ventura-blue to-ventura-darkBlue text-white py-16 md:py-20">
            <div className="container mx-auto px-4 md:px-6 max-w-4xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">About Ventura Power Washing</h1>
              <p className="text-lg md:text-xl leading-relaxed text-white/95">
                Ventura Power Washing is a locally owned exterior cleaning company that provides pressure washing, soft washing, window, gutter, solar panel, carpet, and dumpster pad cleaning for Ventura County homeowners, HOAs, and property managers, helping them achieve cleaner, longer-lasting properties and dependable, on-time service through hot-water equipment, biodegradable detergents, and 300+ completed jobs.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-white text-ventura-darkBlue hover:bg-white/90">
                  <Link to="/free-estimate">Get a Free Estimate</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-white text-white bg-transparent hover:bg-white/10">
                  <a href={PHONE_HREF}><Phone className="mr-2 h-4 w-4" />{PHONE_DISPLAY}</a>
                </Button>
              </div>
            </div>
          </header>

          <div className="container mx-auto px-4 md:px-6 max-w-4xl py-12 space-y-16">
            {/* Quick stats */}
            <section aria-label="Company at a glance">
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map((s) => (
                  <div key={s.label} className="rounded-lg bg-ventura-lightGray p-4 text-center">
                    <dt className="sr-only">{s.label}</dt>
                    <dd className="text-3xl font-bold text-ventura-blue">{s.value}</dd>
                    <dd className="text-sm text-gray-600 mt-1">{s.label}</dd>
                  </div>
                ))}
              </dl>
            </section>

            {/* Brand identity */}
            <section aria-labelledby="identity">
              <SectionHeading id="identity">Our Identity</SectionHeading>
              <dl className="grid gap-4 sm:grid-cols-2">
                {[
                  ['Personality', 'Dependable, straightforward, and neighborly'],
                  ['Tone', 'Clear, professional, and honest'],
                  ['Values', 'Reliability, safety for surfaces and landscaping, and transparent pricing'],
                  ['Promise', 'We show up on time, communicate clearly, and deliver professional results every time.'],
                  ['Philosophy', 'Use the gentlest method that gets the job done, and protect the property and the environment while doing it.'],
                  ['Mission', 'Bring a higher standard of exterior cleaning to Ventura County.'],
                ].map(([term, def]) => (
                  <div key={term} className="rounded-lg border border-gray-200 p-4">
                    <dt className="font-semibold text-gray-900">{term}</dt>
                    <dd className="text-gray-600 mt-1">{def}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-4 text-gray-700">
                <strong>Positioning:</strong> For Ventura County homeowners, HOAs, and property managers, Ventura Power Washing is the insured, hot-water, eco-safe exterior cleaner that protects delicate surfaces and never requires a long-term contract.
              </p>
            </section>

            {/* Founder */}
            <section aria-labelledby="team">
              <SectionHeading id="team">The Team Behind Ventura Power Washing</SectionHeading>
              <div className="grid md:grid-cols-[220px_1fr] gap-8 items-start">
                <figure>
                  <img
                    src="/team/brad-goldston.jpg"
                    alt="Brad, founder of Ventura Power Washing, smiling in a dark suit and light blue shirt"
                    width={640}
                    height={640}
                    loading="lazy"
                    className="rounded-lg w-full max-w-[220px] aspect-square object-cover"
                  />
                  <figcaption className="text-sm text-gray-500 mt-2">Brad, Founder</figcaption>
                </figure>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    <strong>Founder:</strong> Brad started Ventura Power Washing in 2024. He lives in Thousand Oaks and runs multiple businesses.
                  </p>
                  <p>
                    <strong>Origin story:</strong> Brad built the company to bring a higher standard of exterior cleaning to Ventura County by combining commercial-grade equipment, eco-safe methods, and reliable local service.
                  </p>
                  <p>
                    <strong>Operating philosophy:</strong> Homeowners, HOAs, and property managers need a dependable partner. That means showing up on time, communicating clearly, and delivering professional results every time.
                  </p>
                  <p>
                    <strong>Brand promise:</strong> A clean property, no surprises, and no long-term contract required.
                  </p>
                </div>
              </div>
              <div className="grid md:grid-cols-[220px_1fr] gap-8 items-start mt-10">
                <figure>
                  <img
                    src="/team/logan-oliverson.jpg"
                    alt="Logan, cleaning tech at Ventura Power Washing, in a safety vest setting up a ladder next to a dumpster"
                    width={900}
                    height={1200}
                    loading="lazy"
                    className="rounded-lg w-full max-w-[220px] aspect-[3/4] object-cover"
                  />
                  <figcaption className="text-sm text-gray-500 mt-2">Logan, Cleaning Tech</figcaption>
                </figure>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    Logan has been part of our cleaning family for more than five years, originally starting with Hi Rise Services before joining Ventura Power Washing full-time. A Thousand Oaks local through and through, Logan brings a hometown work ethic, a calm professional presence, and a reputation for meticulous results.
                  </p>
                  <p>
                    He specializes in window detailing, pressure washing, and carpet cleaning, with a focus on protecting delicate surfaces and delivering spotless finishes. Logan is known for his consistency: he shows up prepared, communicates clearly with homeowners, and treats every property like it’s his own.
                  </p>
                  <p>
                    Customers often mention his attention to detail, his careful prep work, and the dramatic before/after transformations he delivers. Whether he’s restoring a driveway, brightening a home’s exterior, or bringing carpets back to life, Logan’s work speaks for itself.
                  </p>
                  <p>
                    Outside of work, Logan stays active in the Thousand Oaks community and enjoys spending time outdoors with friends and family. He also loves surfing, playing volleyball, and working on his cars and motorcycles.
                  </p>
                </div>
              </div>
            </section>

            {/* What we do */}
            <section aria-labelledby="what-we-do">
              <SectionHeading id="what-we-do">What Ventura Power Washing Does</SectionHeading>
              <div className="space-y-6">
                {services.map((s) => (
                  <article key={s.name}>
                    <h3 className="text-xl font-semibold text-gray-900">
                      <Link to={s.href} className="hover:text-ventura-blue">{s.name}</Link>
                    </h3>
                    <p className="text-gray-700 mt-1">{s.body}</p>
                  </article>
                ))}
              </div>
            </section>

            {/* Differentiators */}
            <section aria-labelledby="different">
              <SectionHeading id="different">What Makes Ventura Power Washing Different</SectionHeading>
              <div className="space-y-8">
                {differentiators.map((d) => (
                  <article key={d.title}>
                    <h3 className="text-xl font-semibold text-gray-900">{d.title}</h3>
                    <p className="text-gray-700 mt-1">{d.difference}</p>
                    <ul className="mt-2 space-y-1 text-gray-700 list-disc pl-5">
                      <li><strong>What we don’t do:</strong> {d.negative}</li>
                      <li><strong>Compared with:</strong> {d.comparison}</li>
                    </ul>
                  </article>
                ))}
              </div>
            </section>

            {/* ICP */}
            <section aria-labelledby="who">
              <SectionHeading id="who">Who Uses Ventura Power Washing</SectionHeading>
              <div className="space-y-6">
                {segments.map((s) => (
                  <article key={s.name} className="rounded-lg border border-gray-200 p-5">
                    <h3 className="text-xl font-semibold text-gray-900">{s.name}</h3>
                    <ul className="mt-2 space-y-1 text-gray-700 list-disc pl-5">
                      <li><strong>Who:</strong> {s.detail}</li>
                      <li><strong>Pain points:</strong> {s.pain}</li>
                      <li><strong>Typical triggers:</strong> {s.trigger}</li>
                    </ul>
                  </article>
                ))}
              </div>
            </section>

            {/* How it works */}
            <section aria-labelledby="how-it-works">
              <SectionHeading id="how-it-works">How Ventura Power Washing Works</SectionHeading>
              <ol className="space-y-4">
                {steps.map((s, i) => (
                  <li key={s.title} className="flex gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ventura-blue text-white font-semibold">{i + 1}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900">{s.title}</h3>
                      <p className="text-gray-700">{s.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="flex gap-3 rounded-lg bg-ventura-lightGray p-4">
                  <Droplets className="h-5 w-5 text-ventura-blue shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-gray-700"><strong>Water practices:</strong> Controlled runoff, landscaping pre-soaked, neutralizers applied when needed, and a hot-water reclaim system on commercial jobs.</p>
                </div>
                <div className="flex gap-3 rounded-lg bg-ventura-lightGray p-4">
                  <ShieldCheck className="h-5 w-5 text-ventura-blue shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-gray-700"><strong>Protection:</strong> Licensed, with $1M–$2M general liability and full workers’ compensation.</p>
                </div>
                <div className="flex gap-3 rounded-lg bg-ventura-lightGray p-4">
                  <Clock className="h-5 w-5 text-ventura-blue shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-gray-700"><strong>Turnaround:</strong> Most residential jobs are completed the same day. Commercial jobs take 24–48 hours depending on scope.</p>
                </div>
                <div className="flex gap-3 rounded-lg bg-ventura-lightGray p-4">
                  <FileText className="h-5 w-5 text-ventura-blue shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-gray-700"><strong>Agreements:</strong> Per-project pricing. HOA and commercial clients can go month-to-month or per job.</p>
                </div>
              </div>
            </section>

            {/* Social proof */}
            <section aria-labelledby="proof">
              <SectionHeading id="proof">Social Proof &amp; Results</SectionHeading>
              <div className="grid gap-4 md:grid-cols-3">
                {testimonials.map((t) => (
                  <figure key={t.quote} className="rounded-lg border border-gray-200 p-5">
                    <blockquote className="text-gray-700">“{t.quote}”</blockquote>
                    <figcaption className="text-sm text-gray-500 mt-3">— {t.who}</figcaption>
                  </figure>
                ))}
              </div>
              <div className="mt-10 space-y-8">
                {transformations.map((t) => (
                  <figure key={t.title}>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <img src={t.before} alt={t.beforeAlt} loading="lazy" className="rounded-lg w-full aspect-[4/3] object-cover" />
                        <p className="text-sm font-medium text-gray-600 mt-1">Before</p>
                      </div>
                      <div>
                        <img src={t.after} alt={t.afterAlt} loading="lazy" className="rounded-lg w-full aspect-[4/3] object-cover" />
                        <p className="text-sm font-medium text-gray-600 mt-1">After</p>
                      </div>
                    </div>
                    <figcaption className="text-gray-700 mt-2"><strong>{t.title}:</strong> {t.caption}</figcaption>
                  </figure>
                ))}
              </div>
              <p className="mt-8 text-gray-700">
                <strong>By the numbers:</strong> 300+ jobs completed since 2024, a 1-hour response time during business hours, and quotes delivered within 24 hours.
              </p>
            </section>

            {/* Key facts */}
            <section aria-labelledby="key-facts">
              <SectionHeading id="key-facts">Key Facts</SectionHeading>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-left text-sm md:text-base">
                  <tbody className="divide-y divide-gray-200">
                    {keyFacts.map(([label, value]) => (
                      <tr key={label}>
                        <th scope="row" className="bg-ventura-lightGray px-4 py-3 font-semibold text-gray-900 w-1/3 align-top">{label}</th>
                        <td className="px-4 py-3 text-gray-700">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Why we exist / how we think */}
            <section aria-labelledby="why">
              <SectionHeading id="why">Why We Exist</SectionHeading>
              <p className="text-gray-700 leading-relaxed">
                Property owners deserve exterior cleaning without uninsured crews, surprise contracts, or high-pressure damage to stucco and paint. We exist to be the dependable option: a local company that answers quickly, quotes clearly, and cleans in a way that protects your property.
              </p>
            </section>

            <section aria-labelledby="think">
              <SectionHeading id="think">How We Think</SectionHeading>
              <ul className="space-y-2 text-gray-700 list-disc pl-5">
                <li>Match the method to the surface. Hard concrete gets hot water; stucco and roofs get soft wash.</li>
                <li>Protect what is around the job: landscaping, drains, and neighbors.</li>
                <li>Price it clearly up front and earn repeat work instead of requiring contracts.</li>
                <li>Say no when a job is not right for us, such as asbestos siding or mold remediation.</li>
              </ul>
            </section>

            {/* Glossary */}
            <section aria-labelledby="glossary">
              <SectionHeading id="glossary">Glossary of Terms</SectionHeading>
              <dl className="grid gap-4 sm:grid-cols-2">
                {glossary.map(([term, def]) => (
                  <div key={term}>
                    <dt className="font-semibold text-gray-900">{term}</dt>
                    <dd className="text-gray-600">{def}</dd>
                  </div>
                ))}
              </dl>
            </section>

            {/* Misconceptions */}
            <section aria-labelledby="misconceptions">
              <SectionHeading id="misconceptions">Common Misconceptions</SectionHeading>
              <dl className="space-y-4">
                {misconceptions.map(([myth, fact]) => (
                  <div key={myth}>
                    <dt className="font-semibold text-gray-900">Myth: {myth}</dt>
                    <dd className="text-gray-700">Reality: {fact}</dd>
                  </div>
                ))}
              </dl>
            </section>

            {/* Guardrails */}
            <section aria-labelledby="guardrails">
              <SectionHeading id="guardrails">What We Don’t Do</SectionHeading>
              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  ['Services we do not offer', notOffered.services],
                  ['Surfaces we do not pressure wash', notOffered.surfaces],
                  ['Chemicals we do not use', notOffered.chemicals],
                  ['Claims we will never make', notOffered.claims],
                  ['Areas we do not serve', notOffered.areas],
                ].map(([title, items]) => (
                  <div key={title as string} className="rounded-lg border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-900">{title as string}</h3>
                    <ul className="mt-2 list-disc pl-5 text-gray-700 space-y-1">
                      {(items as string[]).map((i) => <li key={i}>{i}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
            </section>

            {/* Competitors */}
            <section aria-labelledby="competitors">
              <SectionHeading id="competitors">How We Compare</SectionHeading>
              <div className="space-y-6">
                {competitors.map((c) => (
                  <article key={c.name}>
                    <h3 className="text-lg font-semibold text-gray-900">{c.name}</h3>
                    <ul className="mt-1 list-disc pl-5 text-gray-700 space-y-1">
                      <li><strong>What they do:</strong> {c.does}</li>
                      <li><strong>How we differ:</strong> {c.differs}</li>
                      <li><strong>What we intentionally avoid:</strong> {c.avoid}</li>
                      <li><strong>Where we do better:</strong> {c.better}</li>
                    </ul>
                  </article>
                ))}
              </div>
            </section>

            {/* Service areas */}
            <section aria-labelledby="areas">
              <SectionHeading id="areas">Service Areas</SectionHeading>
              <p className="text-gray-700 mb-4">
                We are based in Ventura County. Coastal neighborhoods deal with salt air and marine-layer algae, while inland communities deal with dust, heat, and leaf litter. Stucco, concrete, pavers, and tile roofs are the most common surfaces we clean.
              </p>
              <h3 className="font-semibold text-gray-900">Cities</h3>
              <ul className="mt-2 flex flex-wrap gap-2">
                {cities.map((c) => (
                  <li key={c}>
                    {cityLinks[c] ? (
                      <Link to={cityLinks[c]} className="inline-block rounded-full bg-ventura-lightGray px-3 py-1 text-sm text-ventura-darkBlue hover:underline">{c}</Link>
                    ) : (
                      <span className="inline-block rounded-full bg-ventura-lightGray px-3 py-1 text-sm text-gray-700">{c}</span>
                    )}
                  </li>
                ))}
              </ul>
              <h3 className="font-semibold text-gray-900 mt-6">Neighborhoods</h3>
              <ul className="mt-2 flex flex-wrap gap-2">
                {neighborhoods.map((n) => (
                  <li key={n} className="rounded-full bg-ventura-lightGray px-3 py-1 text-sm text-gray-700">{n}</li>
                ))}
              </ul>
              <p className="mt-6 text-gray-700">
                We also take on select projects across Los Angeles, Orange, San Diego, and Santa Barbara counties. Call {PHONE_DISPLAY} to confirm availability. We do not serve Northern California or the High Desert.
              </p>
            </section>

            {/* FAQ */}
            <section aria-labelledby="faq">
              <SectionHeading id="faq">Frequently Asked Questions</SectionHeading>
              <dl className="space-y-5">
                {faqs.map((f) => (
                  <div key={f.question}>
                    <dt className="font-semibold text-gray-900">{f.question}</dt>
                    <dd className="text-gray-700 mt-1">{f.answer}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-6 text-gray-700">
                More answers on our <Link to="/faq" className="text-ventura-blue hover:underline">full FAQ page</Link>.
              </p>
            </section>

            {/* CTA */}
            <section aria-labelledby="cta" className="rounded-lg bg-ventura-lightGray p-8 text-center">
              <h2 id="cta" className="text-2xl font-bold text-gray-900">Ready for a cleaner property?</h2>
              <p className="text-gray-700 mt-2">Free estimates, quotes within 24 hours, and no long-term contract.</p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg" className="bg-ventura-blue hover:bg-ventura-darkBlue">
                  <Link to="/free-estimate">Get a Free Estimate</Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href={PHONE_HREF}><Phone className="mr-2 h-4 w-4" />{PHONE_DISPLAY}</a>
                </Button>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AboutPage;
